import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, concat, Observable, of } from 'rxjs';
import { uniqBy } from 'lodash-es';
import { map } from 'rxjs/operators';
import { OverlayConfig } from '@angular/cdk/overlay';
import { IIdName } from '@nimbus-library/features/shared/models/interfaces';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';

export class IInputAutocompleteOption implements IIdName<string> {
  public id: string;
  public name: string;
  public disabled?: boolean;
  public disabledItemTooltip?: string;
  public warningItemTooltip?: string;
  public isBeta?: boolean;
  public isGroup?: boolean;
  public infoMessage?: string;
  public hidden?: boolean;
}

@Component({
  standalone: false,
  selector: 'nimbus-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputAutocompleteComponent),
    },
  ],
})
export class InputAutocompleteComponent extends IInputComponent<string> implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(MatAutocomplete) public autocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) public autocompleteTrigger: MatAutocompleteTrigger;

  @Output()
  public afterViewInit: EventEmitter<NgControl> = new EventEmitter<NgControl>();

  @Input()
  public warningMessage: string;
  @Input()
  public disabledItemTooltipText: string;
  @Input()
  public disabledTooltipText: string;
  @Input()
  public panelClass: string;
  @Input()
  public tooltipProp: string = 'name';
  @Input()
  public hasBackdrop: boolean = false;
  @Input()
  public overlayPanelClass: string;
  @Input()
  public itemTemplate: TemplateRef<unknown>;
  @Input()
  public floatLabel: 'auto' | 'always' = 'auto';
  @Input()
  public keepSearchOnOpen: boolean;
  @Input()
  public isRemoveOnlyCase: boolean = false;
  @Input()
  public noRecordsItem = {
    id: 'no_records',
    name: TranslationKeys.GeneralGridNoRecords,
    disabled: true,
    isGroup: false,
    disabledItemTooltip: TranslationKeys.GeneralGridNoRecords,
  };
  @Input()
  public searchEmptyItem = {
    id: 'no_records',
    name: TranslationKeys.GeneralGridTypeToSearch,
    disabled: true,
    isGroup: false,
    hidden: true,
    disabledItemTooltip: TranslationKeys.GeneralGridTypeToSearch,
  };
  @Output()
  public cleanValue: EventEmitter<void> = new EventEmitter<void>();

  private readonly emptyValue = '';
  public _options: IInputAutocompleteOption[] = [];
  public emptyPanel$: Observable<boolean>;
  public autoCompleteControl = new FormControl<IInputAutocompleteOption | string>(null);
  public filteredOptions$: BehaviorSubject<IInputAutocompleteOption[]> = new BehaviorSubject<IInputAutocompleteOption[]>([]);
  public selectedValue: IInputAutocompleteOption = null;

  public showTemplate$ = concat(
    of(this.autoCompleteControl.value),
    this.autoCompleteControl.valueChanges.pipe(map((c) => this.itemTemplate && c && typeof c !== 'string')),
  );

  @Input()
  public get value(): string {
    return this.innerValue;
  }

  @Input()
  @HostBinding('class.disabled')
  public get disabled(): boolean {
    return this.isDisabled;
  }

  @Input()
  public get isDisabled(): boolean {
    return this.innerDisabled;
  }

  @Input()
  public set options(value: IInputAutocompleteOption[]) {
    this._options = value || [];
    this._options = this._options.map((item) => ({ ...item, name: this.translateServ.instant(item.name) }));
    this.filteredOptions$.next(this._options);
    if (this.innerValue) {
      this.onValueChanged(this.innerValue);
      this.markForCheck();
    }
  }

  public constructor(changeDetectionRef: ChangeDetectorRef, injector: Injector, translateService: TranslateService) {
    super(changeDetectionRef, injector, translateService);
  }

  @Input() public displayFn: (val: IInputAutocompleteOption) => string = (item: IInputAutocompleteOption) => item?.name;

  public get options(): IInputAutocompleteOption[] {
    return this._options;
  }

  public get disabledItemSelected(): boolean {
    if (!this.autoCompleteControl.value || typeof this.autoCompleteControl.value === 'string') {
      return false;
    }

    return this.autoCompleteControl.value.disabled;
  }

  public set isDisabled(value: boolean) {
    this.innerDisabled = value;
    if (value) {
      this.autoCompleteControl.disable();
    } else {
      this.autoCompleteControl.enable();
    }
  }

  public set disabled(value: boolean) {
    this.isDisabled = value;
    this.stateChange.emit();
  }

  public set value(value: string) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.propagateChange(value);
    }
    this.onValueChanged(this.innerValue);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.emptyPanel$ = this.filteredOptions$.pipe(map((options) => options.length === 1 && options.includes(this.searchEmptyItem)));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && !!changes.options.currentValue) {
      this.onValueChanged(this.value);
    }
  }

  public ngAfterViewInit(): void {
    this.afterViewInit.emit(this.ngControl);

    if (this.hasBackdrop || this.overlayPanelClass) {
      const originalConfigFn: () => OverlayConfig = (this.autocompleteTrigger as any)._getOverlayConfig;
      (this.autocompleteTrigger as any)._getOverlayConfig = (): OverlayConfig => {
        const config = originalConfigFn.call(this.autocompleteTrigger);
        if (this.hasBackdrop) {
          config.hasBackdrop = true;
          config.backdropClass = 'nimbus-input-autocomplete-backdrop';
        }
        if (this.overlayPanelClass) {
          const existing = Array.isArray(config.panelClass) ? config.panelClass : config.panelClass ? [config.panelClass] : [];
          config.panelClass = [...existing, this.overlayPanelClass];
        }
        return config;
      };
    }
  }

  public writeValue(value: string): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
    this.onValueChanged(this.innerValue);
    this.markForCheck();
  }

  public onValueChanged<T>(value: T): void {
    const selectedValue = (value || value === 0) && this.options ? this.options.find((item) => item.id === value) : null;
    const autoCompleteValue = this.autoCompleteControl.value;
    if (!this.autocomplete?.isOpen && (typeof autoCompleteValue !== 'string' || autoCompleteValue === this.emptyValue)) {
      this.autoCompleteControl.setValue(selectedValue);
    }
  }

  public onOptionSelected(selection: MatAutocompleteSelectedEvent): void {
    if (selection.option.value) {
      this.value = selection.option.value.id;
    }
  }

  public onClearValueClick(event: Event): void {
    super.onClearValueClick(event);
    this.selectedValue = null;
    if (this.isRemoveOnlyCase) {
      this.cleanValue.emit();
    }
    this.autoCompleteControl.setValue(null);
    this.autocompleteTrigger.openPanel();
  }

  public setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
  }

  public onClose(): void {
    const itemSelected = !(typeof this.autoCompleteControl.value === 'string');
    if (!itemSelected && this.value) {
      this.options = (this.options?.length ? uniqBy([...this.options, this.selectedValue], 'id') : [this.selectedValue]).filter(
        (i) => i && i.id !== 'no_records',
      );
      this.autoCompleteControl.setValue(this.selectedValue);
    }
  }

  public onOpen(inputRef: HTMLInputElement): void {
    inputRef.select();
    this.autoCompleteControl.setValue(this.emptyValue, { emitEvent: !this.keepSearchOnOpen });
    this.selectedValue = this.options.find((item) => item.id === this.value);
  }

  public closePanel(): void {
    this.autocomplete.showPanel = false;
  }

  public onTemplateClick(): void {
    this.autocompleteTrigger.openPanel();
  }
}
